import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import { salesService } from '../../services/sales.service';
import { customerService } from '../../services/customer.service';
import api from '../../services/api';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';

interface SOLine {
  productId: number | '';
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  lineTotal: number;
}

const emptyLine: SOLine = { productId: '', productName: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 };

const SalesOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [form, setForm] = useState({
    customerId: '' as any,
    shippingAddress: '',
    billingAddress: '',
    notes: '',
    currencyCode: 'USD',
    exchangeRate: 1
  });
  const [lines, setLines] = useState<SOLine[]>([{ ...emptyLine }]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [custRes, prodRes] = await Promise.all([
        customerService.getAll(),
        api.get('/products'),
      ]);
      setCustomers(custRes);
      setProducts(prodRes.data);
    } catch (err) { console.error('Error loading base data:', err); }

    // Load currencies separately so it doesn't break the form
    try {
      const currRes = await settingsService.getCurrencies();
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def) setForm(f => ({ ...f, currencyCode: def.code }));
    } catch (err) { console.error('Currencies not available:', err); }
  };

  const updateLine = (index: number, field: keyof SOLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'productId') {
      const product = products.find((p: any) => p.id === value);
      if (product) {
        updated[index].productName = product.name;
        updated[index].unitPrice = product.unitPrice || 0;
      }
    }
    const lt = updated[index].quantity * updated[index].unitPrice - updated[index].discount;
    updated[index].lineTotal = lt + lt * updated[index].taxRate / 100;
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { ...emptyLine }]);
  const removeLine = (i: number) => { if (lines.length > 1) setLines(lines.filter((_, idx) => idx !== i)); };

  const subTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice - l.discount, 0);
  const taxTotal = lines.reduce((s, l) => { const net = l.quantity * l.unitPrice - l.discount; return s + net * l.taxRate / 100; }, 0);
  const grandTotal = subTotal + taxTotal;

  const sym = currencies.find(c => c.code === form.currencyCode)?.symbol || '$';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validLines = lines.filter(l => l.productId && l.quantity > 0);
    if (validLines.length === 0) { setError('At least one line item is required'); return; }

    try {
      await salesService.createOrder({
        customerId: form.customerId,
        shippingAddress: form.shippingAddress,
        billingAddress: form.billingAddress,
        notes: form.notes,
        currencyCode: form.currencyCode,
        exchangeRate: form.exchangeRate,
        orderLines: validLines.map(l => ({
          productId: l.productId,
          productName: l.productName,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discount: l.discount,
          taxRate: l.taxRate,
          lineTotal: l.lineTotal
        }))
      } as any);
      navigate('/sales-orders');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating sales order');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('sales_orders.new')}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/sales-orders')}>Back</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required select label={t('sales_orders.customer')} value={form.customerId}
          onChange={e => setForm({ ...form, customerId: e.target.value })}>
          <MenuItem value="">{t('common.select')}</MenuItem>
          {customers.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.code} - {c.name}</MenuItem>)}
        </TextField>
        <TextField select label={t('common.currency')} value={form.currencyCode} sx={{ minWidth: 150 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value })}>
          {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
          {currencies.length === 0 && <MenuItem value="USD">USD ($)</MenuItem>}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('sales_orders.shipping_address')} value={form.shippingAddress}
          onChange={e => setForm({ ...form, shippingAddress: e.target.value })} />
        <TextField fullWidth label={t('sales_orders.billing_address')} value={form.billingAddress}
          onChange={e => setForm({ ...form, billingAddress: e.target.value })} />
      </Box>

      <TextField fullWidth label={t('common.notes')} value={form.notes}
        onChange={e => setForm({ ...form, notes: e.target.value })} sx={{ mb: 2 }} />

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Order Lines</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>{t('sales_orders.product')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.quantity')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.unit_price')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.discount')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.tax_pct')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
              <TableCell sx={{ width: '50px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField select fullWidth size="small" value={line.productId}
                    onChange={e => updateLine(idx, 'productId', e.target.value)}>
                    <MenuItem value="">Select Product</MenuItem>
                    {products.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.sku} - {p.name}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.quantity}
                  onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} inputProps={{ min: 1 }} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.unitPrice}
                  onChange={e => updateLine(idx, 'unitPrice', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.discount}
                  onChange={e => updateLine(idx, 'discount', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.taxRate}
                  onChange={e => updateLine(idx, 'taxRate', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{line.lineTotal.toFixed(2)}</TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                    <Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{subTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{taxTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>Total:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{sym}{grandTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Button startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>{t('common.add_line')}</Button>
      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large">{t('sales_orders.create')}</Button>
      </Box>
    </Box>
  );
};

export default SalesOrderForm;
